import { z } from 'zod';
import { ChapiPriority } from './chapi.js';
import { isRequestFor, parseQuickAdd, requestTitle } from './myday.js';
import { PersonRef } from './people.js';
/**
 * Chapi inside chat (docs/adr/0079, Nathan approved 20 Oct 2026). Three helpers, all human in the loop:
 *  1. a nudge at the top of a channel's or DM's side panel ("Payment App: 2 overdue, 1 due tomorrow"), counted by rules
 *     from work the viewer can see, with one-tap Remind (drafted, then posted once the person confirms), Show overdue and
 *     Summarise this week;
 *  2. @Chapi in the composer: Chapi answers in the conversation for everyone in it, or only to the asker when the answer
 *     touches something some people there can't see; anything that changes data waits on a confirm card that only the
 *     asker can press;
 *  3. spotting tasks in chat: "I'll fix the refund bug tomorrow" shows its author (only) "Make this a task?".
 */
// --- @Chapi -----------------------------------------------------------------------------------------------------
/** "@Chapi" anywhere in a message, as its own word (any case). */
export const CHAPI_MENTION = /(^|[^\p{L}\p{N}_@])@chapi(?![\p{L}\p{N}_])/iu;
/** Whether a message asks Chapi something. */
export function mentionsChapi(body) {
    return CHAPI_MENTION.test(body);
}
/** The question in a message that mentions @Chapi: the words without the "@Chapi", one line. */
export function chapiQuestion(body) {
    return body.replace(new RegExp(CHAPI_MENTION.source, 'giu'), '$1').replace(/^[\s,:;-]+/, '').replace(/\s+/g, ' ').replace(/\s+([,.;:!?])/g, '$1').trim();
}
export const AskChapiInChatInput = z
    .object({
    channelId: z.uuid(),
    /** The asker's own message in this channel that mentions @Chapi. Chapi's answer quotes it. */
    messageId: z.uuid().optional(),
    /** A question with no message (the nudge's "Summarise this week"): always answered only to the asker. */
    text: z.string().trim().min(1).max(2000).optional(),
})
    .refine((v) => !!v.messageId !== !!v.text, { message: 'Pass either a message or a question' });
export const ChapiChatActionState = z.enum(['proposed', 'confirming', 'confirmed', 'dismissed', 'failed']);
const actionBase = {
    state: ChapiChatActionState,
    /** After confirming: what was made or posted ("PAY-12", "Reminder posted"); after failing, why. */
    result: z.string().max(200).nullable(),
    /** The task made or changed, or the message posted. */
    resultId: z.uuid().nullable(),
};
/** A task to create in this conversation's project or client (WorkService.create, as the asker). */
export const ChapiChatTaskAction = z.object({
    kind: z.literal('task'),
    title: z.string().max(200),
    description: z.string().max(4000),
    projectId: z.uuid().nullable(),
    projectName: z.string().nullable(),
    assignee: PersonRef.nullable(),
    dueOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    priority: ChapiPriority.nullable(),
    /** Stage and section when the question names them (docs/adr/0094). Optional: answers stored before have neither. */
    statusId: z.uuid().nullable().optional(),
    statusLabel: z.string().max(40).nullable().optional(),
    sectionId: z.uuid().nullable().optional(),
    sectionName: z.string().max(80).nullable().optional(),
    ...actionBase,
});
/** Assign a task, or change its status (WorkService.update, as the asker). */
export const ChapiChatUpdateAction = z.object({
    kind: z.literal('update'),
    workItemId: z.uuid(),
    key: z.string(),
    title: z.string(),
    /** Set to assign it (null to leave unassigned is not offered). */
    assignee: PersonRef.nullable(),
    statusId: z.uuid().nullable(),
    statusLabel: z.string().nullable(),
    ...actionBase,
});
/** A friendly reminder posted in this conversation, as the asker, mentioning the person. */
export const ChapiChatReminderAction = z.object({
    kind: z.literal('reminder'),
    person: PersonRef,
    /** What will be posted, mentions as `<@id>` tokens. The asker can edit it before confirming. */
    text: z.string().max(2000),
    ...actionBase,
});
export const ChapiChatAction = z.discriminatedUnion('kind', [ChapiChatTaskAction, ChapiChatUpdateAction, ChapiChatReminderAction]);
/**
 * Chapi's answer in a conversation. `channel`: everyone who can read the conversation sees it, as a Chapi message
 * quoting the question. `private`: only the asker sees it; `privateReason` says why.
 */
export const ChapiChatReply = z.object({
    id: z.uuid(),
    channelId: z.uuid(),
    asker: PersonRef,
    questionMessageId: z.uuid().nullable(),
    /** The question as asked (the message's words without "@Chapi", or the private question). */
    question: z.string(),
    visibility: z.enum(['private', 'channel']),
    /**
     * `restricted`: the answer touches work or channels some people here can't see. `asked_privately`: asked without a
     * message (the nudge's Summarise). `failed`: Chapi couldn't answer; only the asker hears about it.
     */
    privateReason: z.enum(['restricted', 'asked_privately', 'failed']).nullable(),
    status: z.enum(['thinking', 'done', 'failed']),
    text: z.string(),
    error: z.string().nullable(),
    actions: z.array(ChapiChatAction),
    createdAt: z.string(),
});
export const ChapiChatReplyList = z.object({ items: z.array(ChapiChatReply) });
/** Confirm one action of an answer (only the asker). A reminder's text can be edited first. */
export const ConfirmChapiChatActionInput = z.object({ index: z.number().int().min(0).max(9), text: z.string().trim().min(1).max(2000).optional() });
/** Dismiss one action, or (no index) hide a private answer. */
export const DismissChapiChatInput = z.object({ index: z.number().int().min(0).max(9).optional() });
// --- Nudges in the side panel -----------------------------------------------------------------------------------
/**
 * The Chapi card at the top of a side panel. Counts are by rules over work the viewer can see (docs/adr/0074): the
 * project's tasks in a project channel, the client's in a client channel, the tasks made in a team channel, and the
 * tasks the people share in a DM. Null when there's nothing worth saying, Chapi is off there, or it was dismissed today.
 */
export const ChapiNudge = z.object({
    scope: z.enum(['project', 'client', 'team', 'dm']),
    /** "Payment App", "Simonds", "#general", "You two". */
    label: z.string(),
    /** "Payment App: 2 overdue, 1 due tomorrow" or "You two have 4 open tasks, 1 overdue". */
    text: z.string(),
    open: z.number().int(),
    overdue: z.number().int(),
    dueToday: z.number().int(),
    dueTomorrow: z.number().int(),
    /** The overdue tasks, for Show overdue. */
    overdueIds: z.array(z.uuid()),
    /** Who "Remind" would nudge: the person (not the viewer) with the most overdue or due-soon work here. */
    remind: PersonRef.nullable(),
    /** YYYY-MM-DD in the viewer's time zone: the day a dismissal lasts for. */
    day: z.string(),
});
export const ChapiNudgeResponse = z.object({ nudge: ChapiNudge.nullable() });
export const DraftChapiReminderInput = z.object({ userId: z.uuid() });
/**
 * A drafted reminder for the person to read, edit and post themselves (postMessage). Only tasks everyone in the
 * conversation can see are named; `text` holds the mention as a `<@id>` token.
 */
export const ChapiReminderDraft = z.object({
    text: z.string(),
    person: PersonRef,
    items: z.array(z.object({ id: z.uuid(), key: z.string(), title: z.string(), dueDate: z.string().nullable() })),
    /** Chapi tidied the wording (false: the plain template, e.g. when Chapi isn't set up). */
    worded: z.boolean(),
});
// --- Spotting tasks in chat -------------------------------------------------------------------------------------
/** A task Chapi spotted in someone's own message, shown only to them. */
export const TaskSuggestion = z.object({
    id: z.uuid(),
    channelId: z.uuid(),
    messageId: z.uuid(),
    title: z.string(),
    dueOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    /** Null: the author. */
    assignee: PersonRef.nullable(),
    /** The channel's project, for "Edit" in the make-task form. */
    projectId: z.uuid().nullable(),
    createdAt: z.string(),
});
export const TaskSuggestionList = z.object({ items: z.array(TaskSuggestion) });
export const AcceptTaskSuggestionResponse = z.object({ workItem: z.object({ id: z.uuid(), key: z.string() }) });
/** At most this many suggestions per person per hour, and per day. */
export const TASK_SUGGESTIONS_PER_HOUR = 6;
export const TASK_SUGGESTIONS_PER_DAY = 20;
const MENTION = /<@([0-9a-f-]{36})>/gi;
// "I'll", "I will", "I'm going to", "I'm gonna", "let me", optionally after "ok", "sure", "yep".
const COMMIT = /(?:^|[\s,;:(-])(?:i'?ll|i\s+will|i'?m\s+going\s+to|i\s+am\s+going\s+to|i'?m\s+gonna|let\s+me)\s+(.+)$/i;
// Words that can sit between the commitment and its verb.
const ADVERBS = /^(?:also|just|definitely|probably|quickly|then|try\s+to|aim\s+to|make\s+sure\s+to|get\s+to|go\s+ahead\s+and)\s+/i;
// Not a piece of work: "I'll be…", "I'll let you know", "I'll see", "I'll think about it".
const NOT_WORK = /^(?:be|let\s+you\s+know|see|think|keep|need|have\s+to|wait|leave|stop|never|not)\b/i;
const capital = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const tidy = (s) => s.replace(/\s+/g, ' ').replace(/[\s,;:.!-]+$/, '').trim();
/**
 * Spots a task in a chat message, by rules (no model): a commitment by its author with a date ("I'll fix the refund
 * bug tomorrow" → Fix the refund bug, tomorrow, the author), or a request with a date aimed at someone mentioned
 * ("can you send the invoice by Friday @Mia" → Send the invoice, Friday, Mia). The date is read with the same reader as
 * /remind and My day's quick add. Without a date it stays quiet: an open-ended "I'll look into it" is not offered.
 */
export function spotTask(body, authorUserId, now, timeZone) {
    const text = body.trim();
    if (!text || text.length > 600 || text.startsWith('/') || text.includes('```') || mentionsChapi(text))
        return null;
    // A request to someone mentioned (not the author): theirs.
    const mentioned = [...new Set([...text.matchAll(MENTION)].map((m) => m[1].toLowerCase()))].filter((id) => id !== authorUserId.toLowerCase());
    for (const userId of mentioned) {
        if (!isRequestFor(text, userId))
            continue;
        const r = requestTitle(text, now, timeZone);
        const title = tidy(r.title);
        if (r.dueOn && title.length >= 3)
            return { title: capital(title).slice(0, 120), dueOn: r.dueOn, assigneeUserId: userId, kind: 'request' };
    }
    // A commitment by the author.
    const plain = text.replace(MENTION, ' ').replace(/\s+/g, ' ');
    for (const sentence of plain.split(/(?<=[.!?])\s+|\n+/)) {
        const s = sentence.trim();
        if (!s || s.endsWith('?'))
            continue;
        const m = COMMIT.exec(s);
        if (!m)
            continue;
        let rest = m[1].trim();
        for (let i = 0; i < 3; i++)
            rest = rest.replace(ADVERBS, '');
        if (NOT_WORK.test(rest))
            continue;
        const q = parseQuickAdd(rest.replace(/[.!]+$/, ''), now, timeZone);
        const title = tidy(q.title.replace(/\b(please|pls|thanks|cheers)\b/gi, ''));
        if (!q.dueOn || title.length < 3)
            continue;
        return { title: capital(title).slice(0, 120), dueOn: q.dueOn, assigneeUserId: null, kind: 'commitment' };
    }
    return null;
}
// A phrase ends at punctuation, at the next hint, or at "by <day>" / "for <someone>" / "due".
const STOP = String.raw `(?=\s*(?:[,;.!?]|$|\b(?:by|for|due|in\s+the|section|stage|status|p[1-4]|priority)\b))`;
const STAGE = new RegExp(String.raw `(?:^|[\s,;(])(?:in\s+)?(?:stage|status)\s*[:=]?\s+([\p{L}\p{N}][\p{L}\p{N} '&/-]{0,38}?)` + STOP, 'iu');
const SECTION_IN = new RegExp(String.raw `(?:^|[\s,;(])(?:in|under|into)\s+(?:the\s+)?([\p{L}\p{N}][\p{L}\p{N} '&/-]{0,58}?)\s+section\b`, 'iu');
const SECTION_NAMED = new RegExp(String.raw `(?:^|[\s,;(])section\s*[:=]?\s+([\p{L}\p{N}][\p{L}\p{N} '&/-]{0,58}?)` + STOP, 'iu');
const PRIORITY = /(?:^|[\s,;(])(?:priority\s*[:=]?\s*)?(p[1-4])\b/i;
/**
 * Reads the stage, section and priority a task request names, by rules (no model): "stage In progress",
 * "in the Forms section", "P1". What it finds is taken out of the words left for the title. Nothing found is null.
 */
export function taskDraftHints(text) {
    let rest = ` ${text} `;
    const take = (re) => {
        const m = re.exec(rest);
        if (!m)
            return null;
        rest = `${rest.slice(0, m.index)}\u0000${rest.slice(m.index + m[0].length)}`;
        return m[1].trim().replace(/\s+/g, ' ');
    };
    const section = take(SECTION_IN) ?? take(SECTION_NAMED);
    const stage = take(STAGE);
    const p = take(PRIORITY);
    // Commas left beside a phrase that was taken out go with it.
    rest = rest.replace(/[\s,;]*\u0000[\s,;]*/g, ' ').replace(/^[\s,;]+|[\s,;]+$/g, '').replace(/\s+/g, ' ').trim();
    return { stage: stage || null, section: section || null, priority: p ? p.toLowerCase() : null, rest };
}
//# sourceMappingURL=chapi-chat.js.map